import { omit } from 'lodash'
import React from 'react'
import { useMutation, useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import { Box, Flex, Spinner } from 'theme-ui'
import { match, __ } from 'ts-pattern'
import { ReportAction } from '../../@types/report-action'
import {
  actions,
  editReport,
  getReport,
  reportAction,
} from '../../api/reportFunction'
import ConfirmButton from '../../components/ConfirmButton'
import { InputComponent } from '../../components/Input/Input'
import {
  notifyErrorAPI,
  notifyInfo,
  notifySuccess,
} from '../../components/Notif'
import useCurrentUserStore from '../../stores/currentUser'
import { date } from '../../utils/formatter'
import Header from './components/Header'
import InvestigatorActions from './components/InvestigatorActions'
import ReportForm from './components/ReportForm'
import ReviewerActions from './components/ReviewerActions'
import VerificatorActions from './components/VerificatorActions'
import { normalizeApiToForm, normalizeFormToApi } from './normalizer'

function LaporanPengaduanDetail() {
  const { id } = useParams()
  // const isEdit = params['*'].includes('edit')
  const { data, isLoading, refetch } = useQuery(['/reports/:id', id], () =>
    getReport(id)
  )
  const roles = useCurrentUserStore((state) => state.currentUser?.data?.roles)
  const report = data?.data?.data || {}

  const editM = useMutation((data: any) => editReport(id, data), {
    onSuccess: () => refetch(),
    onError: notifyErrorAPI,
  })
  const actionM = useMutation(
    (variables: { id: number; action: actions; payload }) => {
      return reportAction(variables.id, variables.action, variables.payload)
    },
    {
      onError: notifyErrorAPI,
      onSuccess: (data) => refetch(),
    }
  )

  // @todo reduce repetition
  function handleSubmit(vals) {
    const normalized = normalizeFormToApi(vals)

    return editM
      .mutateAsync({
        report: {
          ...omit(report, ['reportLines']),
          reportLinesAttributes: normalized.report.reportLinesAttributes,
        },
      })
      .then(() => notifySuccess('Laporan berhasil diubah'))
  }

  function handleAction(action: ReportAction, payload: Record<any, any> = {}) {
    switch (action) {
      // whistleblower
      case 'REPORT': {
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'report',
            payload: {},
          })
          .then(() => notifySuccess('Laporan telah diajukan ke admin'))
      }
      // verificator
      case 'SEND_TO_REVIEWER': {
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'send-to-reviewer',
            payload: {},
          })
          .then(() => notifySuccess('Laporan telah diteruskan ke UP2'))
      }
      case 'SEND_TO_HONOR_BOARD': {
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'send-to-board',
            payload: {},
          })
          .then(() =>
            notifySuccess('Laporan telah diteruskan ke dewan kehormatan')
          )
      }
      case 'HANDOVER_TO_KPK': {
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'hand-over-to-KPK',
            payload: {},
          })
          .then(() =>
            notifySuccess('Laporan telah diteruskan ke dewan kehormatan')
          )
      }
      case 'RETURN_TO_WHISTLEBLOWER': {
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'return-to-user',
            payload: { remarks: payload.remarks },
          })
          .then(() => notifyInfo('Laporan telah dikembalikan'))
      }
      case 'ARCHIVE': {
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'archive',
            payload: {},
          })
          .then(() => notifyInfo('Laporan telah diarsipkan'))
      }
      // reviewer
      case 'SEND_TO_INVESTIGATOR':
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'send-to-investigator',
            payload: { remarks: payload.remarks },
          })
          .then(() => notifyInfo('Laporan dikirm ke pemeriksa'))
      case 'RETURN_TO_VERIFICATOR':
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'return-to-verificator',
            payload: { remarks: payload.remarks },
          })
          .then(() => notifyInfo('Laporan telah dikembalikan ke UP1'))
      case 'VERDICT_VIOLATION':
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'verdict',
            payload: { violation: true, remarks: payload.remarks },
          })
          .then(() => notifyInfo('Laporan telah terbukti sebagai pelanggaran'))
      case 'VERDICT_NOT_VIOLATION':
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'verdict',
            payload: { violation: false, remarks: payload.remarks },
          })
          .then(() => notifyInfo('Laporan tidak terbukti sebagai pelanggaran'))
      case 'FINISH':
        return actionM
          .mutateAsync({
            id: report.id,
            action: 'finish',
            payload: { suspects: payload.suspects },
          })
          .then(() => notifySuccess('Sanksi telah diberikan'))
    }
  }

  if (isLoading) {
    return (
      <Flex
        sx={{
          minHeight: '80vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner />
      </Flex>
    )
  }

  const initialValues = normalizeApiToForm(report)

  return (
    <Box p="3">
      <Header />
      <ReportForm
        renderHeader={() => {
          return (
            <Flex mb={20}>
              <Box sx={{ flex: '1', mr: 10 }}>
                <InputComponent
                  disabled
                  label="Tanggal Pengaduan"
                  placeholder="Tanggal Pengaduan"
                  value={date(report.createdAt)}
                />
              </Box>
              <Box sx={{ flex: '1', mr: 10 }}>
                <InputComponent
                  disabled
                  label="No. Laporan"
                  placeholder="No. Laporan"
                  value={report.no}
                />
              </Box>
              <Box sx={{ flex: '1', mr: 10 }}>
                <InputComponent
                  disabled
                  label="Status"
                  placeholder="Status"
                  value={match(report.state)
                    .with(
                      'verdict',
                      () =>
                        `${report.state} ${
                          report.violation ? '(violation)' : '(not violation)'
                        }`
                    )
                    .with(__, () => `${report.state}`)
                    .exhaustive()}
                />
              </Box>
            </Flex>
          )
        }}
        report={report}
        disabled={editM.isLoading}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        state={match<any, 'editing' | 'readonly'>([roles, report.state])
          .with(['user', 'need_to_revise'], () => 'editing')
          .otherwise(() => 'readonly')}
        renderActions={(values, validateForm) => {
          return match(`${roles}_${report.state}`)
            .when(
              (s) => /user_(undefined|need_to_revise)/.test(s),
              () => (
                <ConfirmButton
                  text="Laporkan"
                  header="Laporkan ke admin?"
                  onConfirm={() => {
                    return handleSubmit(values).then(() => {
                      return handleAction('REPORT')
                    })
                  }}
                  buttonProps={{ ml: 2 }}
                />
              )
            )
            .when(
              (s) => /(admin_verificator|superadmin)_reported/.test(s),
              () => (
                <VerificatorActions
                  reportType={values.reportType?.valueName}
                  onConfirm={(action) => {
                    return handleSubmit(values).then(() => handleAction(action))
                  }}
                />
              )
            )
            .when(
              (s) =>
                /(admin_reviewer|admin_board_of_honor|superadmin)_verified/.test(
                  s
                ),
              () => <ReviewerActions onConfirm={handleAction} />
            )
            .when(
              (s) => /(admin_investigator|superadmin)_reviewed/.test(s),
              () => <InvestigatorActions onConfirm={handleAction} />
            )
            .when(
              (s) => /(admin_reviewer|admin_board_of_honor)_verdict/.test(s),
              () => (
                <ConfirmButton
                  text="Beri Sanksi"
                  header="Beri sanksi?"
                  onConfirm={() => {
                    return validateForm(values).then(() =>
                      handleSubmit(values).then(() =>
                        handleAction('FINISH', {
                          suspects: values.involvedParties.valueName,
                        })
                      )
                    )
                  }}
                />
              )
            )
            .otherwise(() => null)
        }}
      />
    </Box>
  )
}

export default LaporanPengaduanDetail
